// 🔤 ORDENA LA FRASE — Parte II §0 y §2 del documento maestro.
//
// Spec visual del documento:
//   · "Chips de palabra en un contenedor superior (flexbox wrap), fila vacía
//      inferior con slots punteados."
//   · "Drag-and-drop (o tap-to-place en mobile) con framer-motion para el snap
//      suave."
//   · "Si el orden final no es exacto, animación de 'shake' leve en los chips
//      mal colocados — nunca un error rojo agresivo."
//
// ⚠️ NOTA DE IMPLEMENTACIÓN: el documento sugiere framer-motion, que NO está
//    instalado en este repo. En vez de sumar una dependencia al bundle por un
//    solo efecto, se usa la variante que el propio documento autoriza —
//    "o tap-to-place en mobile" — con el snap resuelto en CSS. NSL es una PWA
//    mobile-first, así que tap-to-place además es el gesto correcto aquí.
import { useMemo, useState } from 'react';

/** Normaliza para comparar: sin puntuación, sin mayúsculas. */
function norm(word) {
  return String(word).toLowerCase().replace(/[.,;:!?]/g, '').trim();
}

export default function SlideOrdenaFrase({ slide, onAnswer, locked }) {
  // El orden esperado se deriva de `answer`, pero filtrado a los tokens que
  // realmente existen entre los chips disponibles. Esto hace la validación
  // robusta ante frases del banco cuyo texto correcto incluye alguna palabra
  // que no se entrega como ficha (p. ej. el artículo "a" en el slide C1-3).
  const expected = useMemo(() => {
    const pool = slide.chips.map(norm);
    const remaining = [...pool];
    const seq = [];
    for (const token of slide.answer) {
      const n = norm(token);
      const idx = remaining.indexOf(n);
      if (idx !== -1) {
        remaining.splice(idx, 1);
        seq.push(n);
      }
    }
    return seq;
  }, [slide]);

  // placed guarda índices del arreglo original de chips, para permitir
  // palabras repetidas sin ambigüedad (p. ej. los dos "I" del slide B1-4).
  const [placed, setPlaced] = useState([]);
  const [shake, setShake] = useState(false);
  const [answered, setAnswered] = useState(false);

  const isPlaced = (i) => placed.includes(i);

  const place = (i) => {
    if (answered || locked || isPlaced(i)) return;
    setPlaced((p) => [...p, i]);
  };

  const unplace = (i) => {
    if (answered || locked) return;
    setPlaced((p) => p.filter((x) => x !== i));
  };

  const submit = () => {
    if (answered || locked) return;
    const attempt = placed.map((i) => norm(slide.chips[i]));
    const correct =
      attempt.length === expected.length &&
      attempt.every((w, i) => w === expected[i]);

    if (!correct) {
      // Shake leve, sin error rojo agresivo — tono del documento.
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }

    setAnswered(true);
    onAnswer(correct);
  };

  const allPlaced = placed.length === slide.chips.length;

  return (
    <>
      <p className="ob-subhead">{slide.prompt}</p>

      {/* Fila de destino con slots */}
      <div className={`ob-order-slots${shake ? ' ob-shake' : ''}`}>
        {placed.length === 0 ? (
          <span className="ob-order-empty">Toca las palabras en orden</span>
        ) : (
          placed.map((i) => (
            <button
              key={`placed-${i}`}
              type="button"
              className="ob-chip ob-chip-placed"
              onClick={() => unplace(i)}
              disabled={answered || locked}
            >
              {slide.chips[i]}
            </button>
          ))
        )}
      </div>

      {/* Pool de chips disponibles */}
      <div className="ob-order-pool">
        {slide.chips.map((word, i) =>
          isPlaced(i) ? null : (
            <button
              key={`chip-${i}`}
              type="button"
              className="ob-chip"
              onClick={() => place(i)}
              disabled={answered || locked}
            >
              {word}
            </button>
          )
        )}
      </div>

      {allPlaced && !answered && (
        <div className="ob-actions">
          <button type="button" className="ob-btn" onClick={submit}>
            Listo
          </button>
        </div>
      )}

      {answered && (
        <div className="ob-correction">
          <span aria-hidden="true">✓</span>
          <span>{slide.answerText}</span>
        </div>
      )}
    </>
  );
}
